import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/components/ui/accordion';
import { cn } from '@/shared/lib/utils';

import type { Addon, AddonCategory } from '../../types/addons';
import { getPriceCalculationType, getCalculationTypeLabel } from '../../utils/addon';
import { Check, Calculator } from 'lucide-react';

interface AddonsListProps {
  addonCategories: AddonCategory[];
  render: (addon: Addon, categoryId: string, selectMax: number) => React.ReactNode;
  animatingBadge?: string | null;
  isMandatoryCategoryComplete?: (categoryId: string) => boolean;
  // Versão enxuta (atendimento/PDV, telas baixas): menos respiro entre grupos
  // e opções, para caberem mais linhas sem rolar.
  compact?: boolean;
}

export function AddonsList({
  addonCategories,
  render,
  animatingBadge,
  isMandatoryCategoryComplete,
  compact = false,
}: AddonsListProps) {
  // Verificação de segurança para evitar erro de undefined
  if (!addonCategories || !Array.isArray(addonCategories)) {
    return null;
  }

  return (
    <div className={cn('w-full max-w-full bg-white rounded-lg', compact ? 'px-3 py-2' : 'p-4')}>
      <Accordion
        type="multiple"
        defaultValue={addonCategories.map(cat => cat.id)}
        className={cn('flex flex-col w-full max-w-full', compact ? 'space-y-2' : 'space-y-4')}
      >
        {addonCategories.map(addonCategory => (
          <AccordionItem
            key={addonCategory.id}
            value={addonCategory.id}
            className="bg-gray-100 rounded-md overflow-hidden border-none"
            data-category-id={addonCategory.id}
          >
            <AccordionTrigger 
              className={cn(
                'flex items-center justify-between bg-gray-100 hover:no-underline border-none shadow-none',
                compact ? 'px-3 py-2' : 'px-4 py-3'
              )}
              data-category-id={addonCategory.id}
            >
              <div className="flex flex-col items-start w-full gap-1">
                <div className="flex items-center gap-2 justify-between w-full">
                  <h3 className="text-night text-sm font-bold">{addonCategory.name}</h3>
                </div>
                {/* Indicação do tipo de cálculo de preço */}
                {getPriceCalculationType(addonCategory) !== 0 && (
                  <span className="text-blue-600 text-xs font-medium flex items-center gap-1">
                    <Calculator className="w-3 h-3" />
                    {getCalculationTypeLabel(getPriceCalculationType(addonCategory))}
                  </span>
                )}
                {(addonCategory.mandatory === 1 ||
                  addonCategory.select_min > 0 ||
                  addonCategory.select_max > 0) && (
                    <span className="text-gray-500 text-xs font-normal">
                      {addonCategory.mandatory === 1 &&
                        addonCategory.select_min === 1 &&
                        addonCategory.select_max === 1
                        ? 'Selecione 1 item'
                        : addonCategory.mandatory === 1 &&
                          addonCategory.select_min > 0 &&
                          addonCategory.select_max > 0
                          ? `Selecione de ${addonCategory.select_min} a ${addonCategory.select_max} itens`
                          : addonCategory.mandatory === 1 && addonCategory.select_max > 0
                            ? `Máximo ${addonCategory.select_max} itens`
                            : addonCategory.mandatory === 1
                              ? 'Selecione pelo menos 1 item'
                              : addonCategory.select_min === addonCategory.select_max &&
                                addonCategory.select_max === 1
                                ? '1 item'
                                : addonCategory.select_min === addonCategory.select_max &&
                                  addonCategory.select_max > 0
                                  ? `${addonCategory.select_min} itens`
                                  : addonCategory.select_min > 0 && addonCategory.select_max > 0
                                    ? `${addonCategory.select_min}-${addonCategory.select_max} itens`
                                    : addonCategory.select_max > 0
                                      ? `Máx ${addonCategory.select_max}`
                                      : 'Opcional'}
                    </span>
                  )}
              </div>
              {addonCategory.mandatory === 1 && (
                <div className="relative">
                  {isMandatoryCategoryComplete?.(addonCategory.id) ? (
                    <Check className="w-5 h-5 text-green-700 font-bold animate-in fade-in duration-300" />
                  ) : (
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] bg-black text-gray-100 font-semibold',
                        animatingBadge === addonCategory.id && 'animate-shake'
                      )}
                      data-mandatory-badge
                    >
                      OBRIGATÓRIO
                    </span>
                  )}
                </div>
              )}
            </AccordionTrigger>
            <AccordionContent className={compact ? 'px-3 pt-0 pb-1' : 'p-4'}>
              <div className={cn('flex flex-col w-full max-w-full', compact ? 'gap-0' : 'gap-3')}>
                {addonCategory.addons.map(addon => (
                  <div key={addon.id} className="w-full max-w-full">
                    {render(addon, addonCategory.id, addonCategory.select_max)}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
